import React, { CSSProperties, useContext, useEffect, useRef } from "react";
import {
  BOT_FORM_FOLLOW_FUTURES_AS_SPOT_ID,
  BOT_FORM_FUTURES_AS_SPOT_NOTICE_ID,
  BOT_FORM_FUTURES_AS_SPOT_CONFIRMATION_ID,
  BOT_FORM_FUTURES_AS_SPOT_MANAGE_ID,
  BOT_FORM_FUTURES_AS_SPOT_HELPER_ID,
} from "testIds";
import { FollowFuturesAsSpotData } from "@shared/features/bots/BotForm/common/hooks/useFollowFuturesAsSpot";
import { AnalyticsPageName, LanguageNameSpaces } from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import Checkbox from "common/Checkbox/Checkbox";
import Text from "common/Typography/Text";
import { HelperTooltip } from "common/Tooltips/HelperTooltip";
import Link from "common/Typography/Link";
import InfoCircleOutlined from "@ant-design/icons/InfoCircleOutlined";
import { typography } from "@shared/common/Themes/typography";
import styled, { useTheme } from "styled-components";
import OverrideTypeToggle from "features/bots/BotForm/common/OverrideTypeToggle";
import {
  OverrideType,
  Steps,
} from "@shared/features/bots/BotForm/common/types";
import { BotFormContext } from "@shared/features/bots/BotForm/common/BotForm.context";
import { FormFieldTitle } from "features/bots/common/styles";
import { takeFuturesAsSpotScrollRequest } from "features/bots/BotForm/Advanced/futuresAsSpotScroll";

const FollowFuturesAsSpot = ({
  data,
  style,
}: {
  data: FollowFuturesAsSpotData;
  style?: CSSProperties;
}) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  if (!data.isEligible) return null;
  return (
    <div style={{ marginTop: 16, ...style }}>
      <CheckboxRow>
        <Checkbox
          checked={data.checked}
          data-testid={BOT_FORM_FOLLOW_FUTURES_AS_SPOT_ID}
          onChange={(event) => data.requestChange(event.target.checked)}
        >
          <Text>{t("follow_futures_as_spot.label")}</Text>
        </Checkbox>
        <FuturesAsSpotHelper showTurnOff={false} />
      </CheckboxRow>
      <CheckboxDescription>
        {t("follow_futures_as_spot.description")}
      </CheckboxDescription>
    </div>
  );
};

const CheckboxRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  .ant-checkbox-wrapper {
    margin-inline-end: 0;
  }
  .anticon {
    color: ${(p) => p.theme.colors.secondaryText};
  }
`;

/** Lines up with the label text, past the checkbox. */
const CheckboxDescription = styled(Text).attrs({
  as: "div",
  $size: typography.fontSizes.S,
})`
  margin-top: 2px;
  padding-inline-start: 24px;
  line-height: 1.45;
  color: ${(p) => p.theme.colors.secondaryText};
`;

export default FollowFuturesAsSpot;

/** The Advanced tab's row: an Off/On toggle like the settings around it. */
export const FuturesAsSpotToggle = ({
  data,
  style,
}: {
  data: FollowFuturesAsSpotData;
  style?: CSSProperties;
}) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const { isNewLook } = useTheme();
  const { tab } = useContext(BotFormContext)!;
  const rowRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (tab !== Steps.ADVANCED || !takeFuturesAsSpotScrollRequest()) return;
    const timeout = setTimeout(() =>
      rowRef.current?.scrollIntoView({ block: "center" })
    );
    return () => clearTimeout(timeout);
  }, [tab]);
  if (!data.isEligible) return null;
  const toggle = (
    <OverrideTypeToggle
      options={[OverrideType.OFF, OverrideType.PERSONAL]}
      showPersonalAsOn={true}
      selectedOption={data.checked ? OverrideType.PERSONAL : OverrideType.OFF}
      onClick={(option) => data.requestChange(option === OverrideType.PERSONAL)}
      style={{ marginLeft: isNewLook ? "auto" : 12 }}
    />
  );
  return (
    <ToggleRow
      ref={rowRef}
      style={style}
      data-testid={BOT_FORM_FOLLOW_FUTURES_AS_SPOT_ID}
      data-enabled={data.checked}
    >
      <FormFieldTitle style={{ margin: 0 }}>
        {t("follow_futures_as_spot.title")}
      </FormFieldTitle>
      <FuturesAsSpotHelper
        showTurnOff={false}
        containerStyle={{ marginLeft: 8 }}
      />
      {toggle}
    </ToggleRow>
  );
};

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  min-height: 24px;
`;

/** One line under the Account field while the bot follows futures on spot. */
export const FuturesAsSpotNotice = ({
  skipsSpotSignals,
  onManage,
}: {
  skipsSpotSignals: boolean;
  onManage?: () => void;
}) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  return (
    <NoticeText role="status" data-testid={BOT_FORM_FUTURES_AS_SPOT_NOTICE_ID}>
      <FuturesAsSpotHelper />
      <span>
        {t("follow_futures_as_spot.notice")}
        {skipsSpotSignals &&
          ` ${t("follow_futures_as_spot.notice_spot_skipped")}`}
        {onManage && " "}
        {onManage && (
          <Link
            as="button"
            type="button"
            $size={typography.fontSizes.S}
            data-testid={BOT_FORM_FUTURES_AS_SPOT_MANAGE_ID}
            onClick={onManage}
            style={{ background: "none", border: 0, padding: 0 }}
          >
            {t("follow_futures_as_spot.change")}
          </Link>
        )}
      </span>
    </NoticeText>
  );
};

const NoticeText = styled(Text).attrs({
  as: "div",
  $size: typography.fontSizes.S,
})`
  display: flex;
  align-items: baseline;
  gap: 6px;
  margin-top: 6px;
  line-height: 1.45;
  color: ${(p) => p.theme.colors.secondaryText};
  .anticon {
    color: ${(p) => p.theme.colors.primaryBlue};
    position: relative;
    top: 1px;
  }
`;

/** Info icon with the full explanation: a tooltip on desktop, a bottom
 * drawer on phones. */
const FuturesAsSpotHelper = ({
  showTurnOff = true,
  containerStyle = { marginLeft: 0 },
}: {
  showTurnOff?: boolean;
  containerStyle?: CSSProperties;
}) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  return (
    <HelperTooltip
      title={t("follow_futures_as_spot.tooltip.title")}
      analyticsPageName={AnalyticsPageName.BOT_FORM}
      analyticsFieldName="Follow Futures On Spot"
      containerStyle={containerStyle}
      maxWidth={420}
      tooltipStyle={{ maxWidth: 420, width: 420 }}
      triggerElement={
        <InfoCircleOutlined
          data-testid={BOT_FORM_FUTURES_AS_SPOT_HELPER_ID}
          style={{ cursor: "help" }}
        />
      }
      description={
        <>
          <div>{t("follow_futures_as_spot.tooltip.intro")}</div>
          <HelperList>
            <li>{t("follow_futures_as_spot.tooltip.longs")}</li>
            <li>{t("follow_futures_as_spot.tooltip.leverage")}</li>
            <li>{t("follow_futures_as_spot.tooltip.shorts")}</li>
            <li>{t("follow_futures_as_spot.tooltip.unlisted")}</li>
            <li>{t("follow_futures_as_spot.tooltip.spot_skipped")}</li>
          </HelperList>
          {showTurnOff && (
            <div>{t("follow_futures_as_spot.tooltip.turn_off")}</div>
          )}
        </>
      }
    />
  );
};

const HelperList = styled.ul`
  margin: 6px 0;
  padding-inline-start: 18px;
  li + li {
    margin-top: 2px;
  }
`;

/** The agreement under the save confirmation's options, next to Save. */
export const FuturesAsSpotConfirmation = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  return (
    <ConfirmationText
      role="status"
      data-testid={BOT_FORM_FUTURES_AS_SPOT_CONFIRMATION_ID}
    >
      <InfoCircleOutlined />
      <span>{t("follow_futures_as_spot.confirmation")}</span>
    </ConfirmationText>
  );
};

const ConfirmationText = styled(Text).attrs({
  as: "div",
  $size: typography.fontSizes.S,
})`
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-top: 16px;
  line-height: 1.5;
  color: ${(p) => p.theme.colors.secondaryText};
  .anticon {
    font-size: ${typography.fontSizes.M};
    position: relative;
    top: 2px;
  }
`;
